import { useCallback, useEffect, useMemo, useRef } from 'react';
import events from '../../../app/cart/events.js';
import { CART_ELEMENT_ID } from '../../../app/cart/constants.js';
import useLocationPathname from './useLocationPathname.js';

const ROOT_SELECTOR = `#${CART_ELEMENT_ID}`;

/**
 * Owns everything about whether the cart panel is showing: the host paths it must
 * stay off, the open/close/toggle actions, the `window` events embedding sites use
 * to drive it, and the keyboard/focus behavior a non-modal dialog has to implement
 * by hand.
 *
 * Open state itself lives in the shared cookie (see `useCartState`), not here — so
 * this hook takes the current value in and returns actions that write it back.
 *
 * @param {object} args
 * @param {string[]} args.excludedPaths host path prefixes where the cart must not appear
 * @param {boolean} args.isCartPanelOpen current persisted open state
 * @param {(update: object) => void} args.updateCartState writes back to the cart cookie
 */
const useCartVisibility = ({ excludedPaths, isCartPanelOpen, updateCartState }) => {
    const openCart = useCallback(() => {
        updateCartState({ isCartPanelOpen: true });
    }, [updateCartState]);

    const closeCart = useCallback(() => {
        updateCartState({ isCartPanelOpen: false });
    }, [updateCartState]);

    const toggleCart = useCallback(() => {
        if (isCartPanelOpen) {
            closeCart();
        } else {
            openCart();
        }
    }, [isCartPanelOpen, openCart, closeCart]);

    // Host sites list paths where the cart would be redundant or actively wrong —
    // checkoutdwa's own checkout flow, for instance. Prefix match, so '/order'
    // covers everything beneath it.
    const pathname = useLocationPathname();
    const shouldSuppressCart = useMemo(
        () => excludedPaths.some((excludedPath) => pathname.startsWith(excludedPath)),
        [excludedPaths, pathname],
    );

    useEffect(function closeOnExcludedPath() {
        if (shouldSuppressCart && isCartPanelOpen) {
            closeCart();
        }
    }, [shouldSuppressCart, closeCart, isCartPanelOpen]);

    useEffect(function manageOpenCartListener() {
        return events.openCart.subscribe(openCart);
    }, [openCart]);

    useEffect(function manageCloseCartListener() {
        return events.closeCart.subscribe(closeCart);
    }, [closeCart]);

    useEffect(function manageToggleCartListener() {
        return events.toggleCart.subscribe(toggleCart);
    }, [toggleCart]);

    /*
     * Escape-to-close and focus restoration.
     *
     * Both come free with a modal dialog. This panel is deliberately non-modal —
     * see the `role="dialog"` comment in CartPanelEmbedded.js — so they are done
     * here by hand. Without Escape, a keyboard user has to tab to the Hide button;
     * without restoring focus, closing the cart drops focus to the top of the host
     * page and loses their place on it.
     */
    const openerRef = useRef(null);

    useEffect(function rememberAndRestoreFocus() {
        if (isCartPanelOpen) {
            // Captured on open rather than at click time so it works however the
            // cart was opened: the host's own trigger, `events.openCart.emit()`, or
            // a cookie change made in another tab.
            openerRef.current = document.activeElement;
            return;
        }

        const opener = openerRef.current;
        openerRef.current = null;
        // Only pull focus back if it is still parked inside the cart. If the visitor
        // has since clicked elsewhere on the host page, moving their focus would be
        // the more disruptive choice.
        if (opener?.isConnected && document.activeElement?.closest?.(ROOT_SELECTOR)) {
            opener.focus();
        }
    }, [isCartPanelOpen]);

    useEffect(function manageEscapeKey() {
        if (!isCartPanelOpen) {
            return undefined;
        }
        const handleKeyDown = (event) => {
            if (event.key === 'Escape') {
                closeCart();
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [isCartPanelOpen, closeCart]);

    return { shouldSuppressCart, openCart, closeCart, toggleCart };
};

export default useCartVisibility;
